'use client';

import React, { forwardRef } from 'react';

import { Checkbox as BaseCheckbox, cn } from '@shared/components/shadcn';
import { FieldMessage } from '../FieldMessage/FieldMessage';
import type { FieldActionElement, MessageType } from '../types';

type CheckboxProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'checked' | 'defaultChecked'> & {
  id: string;
  label?: React.ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  helpMessage?: string;
  helpMessageType?: MessageType;
  bottomActionElement?: FieldActionElement;
  className?: string;
  trackingProps?: Record<string, string>;
  helperTrackingProps?: Record<string, string>;
  alignment?: 'start' | 'center' | 'end';
  'data-testid'?: string;
  'data-track-helper'?: string;
};

const Checkbox = forwardRef(function Checkbox(
  {
    id,
    label,
    checked,
    defaultChecked,
    onCheckedChange,
    disabled,
    helpMessage,
    helpMessageType,
    bottomActionElement,
    className,
    trackingProps,
    helperTrackingProps,
    alignment = 'center',
    'data-track-helper': dataTrackHelper,
    ...props
  }: CheckboxProps,
  ref: React.Ref<HTMLButtonElement>
) {
  const classes = {
    wrapper: 'w-full relative paragraph',
    field: 'flex gap-3',
    checkbox: cn(
      'h-7 w-7 rounded-sm border-gray-300 flex-shrink-0',
      'data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-white',
      checked && 'bg-error-foreground border-primary',
      helpMessageType === 'error' && 'border-error-foreground'
    ),
    label: 'paragraph-explanatory cursor-pointer',
  };

  return (
    <div className={cn(classes.wrapper, className)} {...props}>
      <div className={cn(classes.field, alignment === 'start' && 'items-start', alignment === 'center' && 'items-center', alignment === 'end' && 'items-end')}>
        <BaseCheckbox
          ref={ref}
          id={id}
          checked={checked}
          defaultChecked={defaultChecked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          aria-describedby={helpMessage ? `${id}-message` : undefined}
          aria-invalid={helpMessageType === 'error' || undefined}
          className={classes.checkbox}
          data-testid={`${id}-checkbox`}
          {...trackingProps}
        />
        {label && (
          <label htmlFor={id} className={classes.label}>
            {label}
          </label>
        )}
      </div>

      <FieldMessage
        id={`${id}-message`}
        message={helpMessage}
        messageType={helpMessageType}
        actionElement={bottomActionElement}
        disabled={disabled}
        data-testid={`${id}-error`}
        data-track-helper={dataTrackHelper}
        {...helperTrackingProps}
      />
    </div>
  );
});

export { Checkbox };
